/** Desenha UMA página: canvas e camada de texto, com cancelamento CONJUNTO.
 *
 * O cancelamento conjunto é o ponto. Virar a página rápido com dois ciclos de
 * vida independentes deixa o texto da página anterior sobre o canvas da nova — e
 * como o clipping por `released_locators` decide POR PÁGINA, isso é texto de
 * capítulo não liberado na tela. Por isso um hook só é dono dos dois.
 *
 * A escala é calculada aqui e propagada a três lugares que TÊM de concordar: a
 * caixa do wrapper, o viewport do canvas e o `--scale-factor` que o `TextLayer`
 * do pdf.js lê para dimensionar cada span.
 */
/* eslint-disable react-hooks/immutability -- Este hook faz trabalho IMPERATIVO
   de DOM (dimensionar canvas e wrapper, fixar --scale-factor), que é para o que
   efeito existe. A regra vem do conjunto do React Compiler e mira mutação de
   valores React; nó do DOM obtido por ref é falso positivo aqui. */
import { useEffect, useState } from 'react'

import {
  defaultTextLayerRenderer,
  type PdfDocumentLike,
  type TextLayerRenderer,
} from '@/features/reader/hooks/usePdfDocument'
import { cabeNoOrcamento, dprEfetivo, fitScale, type Tamanho } from '@/features/reader/utils/scale'

export type StatusDaPagina = 'ocioso' | 'pronto' | 'erro'

interface UsePdfPageRenderOptions {
  doc: PdfDocumentLike | null
  page: number
  /** Largura disponível em CSS px; vem do `ResizeObserver` do container. */
  larguraDisponivel: number
  /** Escala pedida pelo zoom; `null` = ajustar à largura. */
  escalaFixa?: number | null
  /** Multiplicador sobre a escala base (tamanho de texto da US-03, zoom).
   *
   * Fator e não escala absoluta: assim a página continua acompanhando a largura
   * da janela em vez de congelar num valor e quebrar a responsividade. */
  fatorDeZoom?: number
  wrapper: HTMLElement | null
  canvas: HTMLCanvasElement | null
  textContainer: HTMLElement | null
  /** `false` para página bloqueada: nem canvas nem texto são montados. */
  habilitado: boolean
  textLayerRenderer?: TextLayerRenderer
  /** Desenho SOBRE a página, depois do pdf.js — é aqui que a watermark entra.
   *
   * Callback e não conhecimento de watermark neste hook: quem desenha a página
   * não precisa saber o que mais vai por cima dela. Recebe as medidas em CSS px
   * e a densidade, porque é nesse sistema de coordenadas que se desenha. */
  aoDesenhar?: (
    contexto: CanvasRenderingContext2D,
    medidas: { largura: number; altura: number; dpr: number },
  ) => void
}

export interface RenderDaPagina {
  status: StatusDaPagina
  /** Tamanho em CSS px da página desenhada. */
  tamanho: Tamanho | null
  escala: number
  erro: Error | null
  /** Conta renders CONCLUÍDOS. É o sinal de "esta página, nesta escala, está
   *  desenhada AGORA".
   *
   *  Contador e não `status`: `status` continua `'pronto'` durante todo o
   *  redesenho — do fim do render anterior ao fim do novo —, então quem pede
   *  uma escala nova e espera `'pronto'` é atendido de imediato, com a caixa
   *  ANTIGA. Foi esse o defeito: a rolagem do zoom ia para o lugar errado.
   *  Comparar contadores distingue "pronto na escala nova" de "ainda pronto na
   *  velha", e é disso que dependem a rolagem do zoom e a retomada de posição. */
  versao: number
}

export function usePdfPageRender({
  doc,
  page,
  larguraDisponivel,
  escalaFixa = null,
  fatorDeZoom = 1,
  wrapper,
  canvas,
  textContainer,
  habilitado,
  textLayerRenderer = defaultTextLayerRenderer,
  aoDesenhar,
}: UsePdfPageRenderOptions): RenderDaPagina {
  const [estado, setEstado] = useState<RenderDaPagina>({
    status: 'ocioso',
    tamanho: null,
    escala: 1,
    erro: null,
    versao: 0,
  })

  useEffect(() => {
    if (!doc || !habilitado || !canvas || !wrapper) return
    if (page < 1 || page > doc.numPages) return

    let cancelado = false
    let renderCanvas: { cancel?: () => void } | null = null
    let renderTexto: { cancel: () => void } | null = null

    const desenhar = async () => {
      const pdfPage = await doc.getPage(page)
      if (cancelado) return

      // Tamanho natural da página (scale 1) para derivar a escala de ajuste.
      const natural = pdfPage.getViewport({ scale: 1 })
      const escalaBase =
        (escalaFixa ?? fitScale(natural, { width: larguraDisponivel, height: 0 }, 'largura')) *
        fatorDeZoom

      const dpr = dprEfetivo(
        natural.width * escalaBase,
        natural.height * escalaBase,
        typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1,
      )
      // Cascata: a densidade já caiu; se ainda não couber, é a ESCALA que cede —
      // canvas em branco no iOS é pior que página menor.
      let escala = escalaBase
      while (escala > 0.2 && !cabeNoOrcamento(natural, escala, dpr)) escala -= 0.1

      const viewport = pdfPage.getViewport({ scale: escala })
      if (cancelado) return

      // O wrapper define a CAIXA da página, e o --scale-factor é lido pelo
      // TextLayer para dimensionar cada span. Escrita por CSSOM (`setProperty`),
      // que a CSP não cobre — a proibição de estilo inline do CLAUDE.md §11 é do
      // painel HTMX, servido pelo backend.
      wrapper.style.width = `${viewport.width}px`
      wrapper.style.height = `${viewport.height}px`
      wrapper.style.setProperty('--scale-factor', String(escala))

      canvas.width = Math.floor(viewport.width * dpr)
      canvas.height = Math.floor(viewport.height * dpr)
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`

      const contexto = canvas.getContext('2d')
      if (!contexto) throw new Error('Canvas 2D indisponível')

      const tarefa = pdfPage.render({
        canvasContext: contexto,
        viewport,
        ...(dpr === 1 ? {} : { transform: [dpr, 0, 0, dpr, 0, 0] }),
      })
      renderCanvas = tarefa
      await tarefa.promise
      if (cancelado) return

      // Depois do pdf.js, senão o render da página apagaria a marca.
      aoDesenhar?.(contexto, {
        largura: viewport.width,
        altura: viewport.height,
        dpr,
      })

      if (textContainer) {
        textContainer.replaceChildren()
        const textContent = await pdfPage.getTextContent()
        if (cancelado) return
        const tarefaTexto = textLayerRenderer({
          container: textContainer,
          textContent,
          viewport,
        })
        renderTexto = tarefaTexto
        await tarefaTexto.promise
        if (cancelado) return
      }

      setEstado((atual) => ({
        status: 'pronto',
        tamanho: { width: viewport.width, height: viewport.height },
        escala,
        erro: null,
        versao: atual.versao + 1,
      }))
    }

    desenhar().catch((err: unknown) => {
      if (cancelado) return
      setEstado((atual) => ({
        ...atual,
        status: 'erro',
        erro: err instanceof Error ? err : new Error('Falha ao desenhar a página'),
      }))
    })

    return () => {
      cancelado = true
      renderCanvas?.cancel?.()
      renderTexto?.cancel()
      // Limpar o texto no cleanup, não no início do próximo ciclo: senão a
      // camada da página anterior fica visível durante todo o novo render.
      if (textContainer) textContainer.replaceChildren()
    }
  }, [
    doc,
    page,
    larguraDisponivel,
    escalaFixa,
    fatorDeZoom,
    wrapper,
    canvas,
    textContainer,
    habilitado,
    textLayerRenderer,
    aoDesenhar,
  ])

  return estado
}
